import type { FieldProps } from 'formik';
import { Field, Formik } from 'formik';
import { useEffect, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import * as Yup from 'yup';

import { getSupportedHardware, HardwareTypes } from '../../../api/Optimise';
import {
  inverterManufacturers,
  manufacturerToSlug,
} from '../../../containers/Onboarding/Connection/inverterManufacturers';
import type { SupportedHardwareManufacturer } from '../../../types/optimise';
import BasicSpinner from '../../BasicSpinner';
import ErrorLabel from '../../ErrorLabel';
import { StyledFormContainer } from '../../Styled';

interface FormValues {
  inverterManufacturer: string;
}

interface SelectManufacturerStepProps {
  onSubmit: (manufacturerSlug: string) => void;
  onCancel: () => void;
}

const validationSchema = Yup.object({
  inverterManufacturer: Yup.string().nullable().trim().required('Please select your inverter manufacturer'),
});

const SelectManufacturerStep = ({ onSubmit, onCancel }: SelectManufacturerStepProps) => {
  const [manufacturerList, setManufacturerList] = useState<SupportedHardwareManufacturer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const res = await getSupportedHardware(HardwareTypes.INVERTER);
        // Only offer manufacturers we actually have a connection form for, so a selection
        // can never fall through to a blank connect step with no way back.
        const supported =
          res?.manufacturer_list?.filter(({ manufacturer }) =>
            Object.prototype.hasOwnProperty.call(inverterManufacturers, manufacturerToSlug(manufacturer))
          ) ?? [];
        setManufacturerList(supported);
      } catch (err) {
        console.error(err);
        setLoadError(true);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const handleSubmit = ({ inverterManufacturer }: FormValues) => {
    onSubmit(manufacturerToSlug(inverterManufacturer));
  };

  return (
    <>
      <h1 className="mb-3" id="inverter-connection-heading">
        Reset my connection details
      </h1>
      <div>
        <p>Please select your inverter manufacturer to reconnect your system.</p>
        {loadError && (
          <ErrorLabel label="Something went wrong loading the list of inverter manufacturers. Please try again." />
        )}
        <StyledFormContainer>
          <Formik<FormValues>
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            initialValues={{ inverterManufacturer: '' }}
            validateOnChange
            validateOnMount
          >
            {({ handleSubmit, isValid }) => (
              <Form onSubmit={handleSubmit}>
                <Field name="inverterManufacturer">
                  {({ field, meta }: FieldProps<string, FormValues>) => (
                    <Form.Group controlId="inverterManufacturer">
                      <Form.Label>
                        Select inverter manufacturer
                        {isLoading && <BasicSpinner />}
                      </Form.Label>
                      <Form.Control
                        as="select"
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        name={field.name}
                        disabled={isLoading || loadError}
                      >
                        <option value="">Select inverter manufacturer</option>
                        {manufacturerList.map(({ manufacturer }) => (
                          <option key={manufacturer} value={manufacturer}>
                            {manufacturer}
                          </option>
                        ))}
                      </Form.Control>
                      {meta.error && meta.touched && <ErrorLabel label={meta.error} />}
                    </Form.Group>
                  )}
                </Field>
                <Form.Group className="mt-2 mb-1 d-flex flex-column">
                  <Button variant="primary" className="mb-2 w-100" disabled={!isValid} type="submit">
                    Confirm
                  </Button>
                  <Button variant="light" type="button" className="w-100" onClick={onCancel}>
                    Cancel
                  </Button>
                </Form.Group>
              </Form>
            )}
          </Formik>
        </StyledFormContainer>
      </div>
    </>
  );
};

export default SelectManufacturerStep;
